import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Card,
  CardContent,
  Divider,
  Grid,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Alert,
  CircularProgress,
  ButtonGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import axios from "../../../utils/axiosInstance";
import GetAppIcon from "@mui/icons-material/GetApp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupIcon from "@mui/icons-material/Group";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import AppsIcon from "@mui/icons-material/Apps";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import EditTechnicianDialog from "./EditTechnicianDialog";
import EditRegionDialog from "./EditRegionDialog";
import AddTechnician from "./AddTechnician";
import AddRegion from "./AddRegion";
import { toast } from "react-toastify";
import ConfirmDialog from "../../ConfirmDialog";
import AssignDevice from "./AssignDeviceForm";
import { useNavigate } from "react-router-dom";
import { sortAlphabetically } from "../../../utils/sortAlphabetically";
import { getUIDescription } from "../customerGroupDetails/AssigningCustomerGroupDevice";

const GroupDetailView = ({
  group,
  onBack,
  onEditGroup,
  onViewModeChange,
  onAddRegion,
  onDeleteRegion,
  onAddTechnician,
  onDeleteTechnician,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md")); // all that they are not big screen meaning "medium + small screens"
  const isTouchDevice = useMediaQuery("(pointer: coarse)");
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const isIpadPro = isTouchDevice && isLargeScreen;
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentView, setCurrentView] = useState("region");
  const [currentTechnician, setCurrentTechnician] = useState(null);
  const [currentRegion, setCurrentRegion] = useState(null);
  const [regionsData, setRegionsData] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [regionsError, setRegionsError] = useState(null);
  const [regionsSearchTerm, setRegionsSearchTerm] = useState("");
  const [confirmRegionOpen, setConfirmRegionOpen] = useState(false);
  const [confirmTechnicianOpen, setConfirmTechnicianOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [editTechnicianDialog, setEditTechnicianDialog] = useState(false);
  const [editRegionDialog, setEditRegionDialog] = useState(false);
  const [addTechnicianDialog, setAddTechnicianDialog] = useState(false);
  const [addRegionDialog, setAddRegionDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [regionRefresher, setRegionRefresher] = useState(0);
  const [updater, setUpdater] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [handleRefresh, setHandleRefresh] = useState(false);

  const [deletingRegion, setDeletingRegion] = useState(false);
  const [deletingTechnician, setDeletingTechnician] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Navigation handlers
  const searchUserTable = () => {
    if (onViewModeChange) onViewModeChange("userList");
  };

  const precisionUnitTable = () => {
    if (onViewModeChange) onViewModeChange("PUG");
  };

  const CustomerGroupTable = () => {
    if (onViewModeChange) onViewModeChange("CustomerGroup");
  };

  const ApplicationTable = () => {
    if (onViewModeChange) onViewModeChange("Apps");
  };

  const confirmDelete = (region) => {
    setSelectedRegion(region);
    setConfirmRegionOpen(true);
  };

  const confirmDeleteRoute = (technician) => {
    setSelectedTechnician(technician);
    setConfirmTechnicianOpen(true);
  };

  useEffect(() => {
    const fetchAllGroups = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/groups?type=geographical`,
          {
            params: { companyId: process.env.REACT_APP_COMPANY_ID },
          },
        );
        const excludedIds = [
          "GROUP-2020-06-06-03-15-14-925896",
          "GROUP-2020-11-26-13-46-30-962731",
        ];

        const filtered = await response.data.filter(
          (group) => !excludedIds.includes(group.id),
        );
        const sortedData = sortAlphabetically(filtered, "name");
        setAllGroups(sortedData);
      } catch (err) {
        console.error("Error fetching groups:", err);
        setAllGroups([]);
      }
    };

    fetchAllGroups();
  }, []);

  const groups = [{ id: group?.id, name: group?.name || "ROOT GROUP" }];
  const regions = regionsData.map((region) => ({
    id: region.id,
    name: region.name,
  }));

  useEffect(() => {
    fetchRegionsAndTechnicians();
  }, [group?.name, updater]);

  const fetchRegionsAndTechnicians = async () => {
    if (!group?.name) {
      setRegionsData([]);
      return;
    }

    setRegionsLoading(true);
    setRegionsError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/groups/tree/${group.id}`,
      );

      const filtered = [response.data];

      const regions =
        filtered.length > 0
          ? (filtered[0].children || []).filter(
              (region) => region.name?.toLowerCase() !== "home",
            )
          : [];
      setRegionsData(regions);
    } catch (err) {
      console.error("Error fetching regions data:", err);
      setRegionsError("Failed to load regions and technicians data");
      setRegionsData([]);
    } finally {
      setRegionsLoading(false);
    }
  };

  const processedRegionsData = regionsData.map((region) => {
    const processedTechnicians = (region.children || []).map((technician) => ({
      ...technician,
      name:
        technician.name?.toLowerCase() === "home"
          ? "HOMEFILTEREDROUTE"
          : technician.name,
    }));

    const regionTotalUnits = processedTechnicians.reduce(
      (total, technician) => total + (technician.children?.length || 0),
      0,
    );

    const hasDashTechnician = processedTechnicians.some(
      (tech) => tech.name === "-",
    );

    const regionTotalRow =
      !hasDashTechnician && processedTechnicians.length > 0
        ? {
            id: `region-total-${region.id}`,
            name: "---",
            children: [],
            isRegionTotal: true,
            unitCount: regionTotalUnits,
          }
        : null;

    const visibleTechnicians = processedTechnicians.filter(
      (tech) => tech.name !== "HOMEFILTEREDROUTE",
    );

    const allTechnicians = regionTotalRow
      ? [regionTotalRow, ...visibleTechnicians]
      : visibleTechnicians;

    return {
      ...region,
      children: allTechnicians,
      allChildrenIncludingHidden: processedTechnicians,
      totalUnits: regionTotalUnits,
    };
  });

  const filteredRegionsData = processedRegionsData
    .map((region) => ({
      ...region,
      children: (region.children || []).filter(
        (technician) =>
          technician.name
            ?.toLowerCase()
            .includes(regionsSearchTerm.toLowerCase()) ||
          region.name
            ?.toLowerCase()
            .includes(regionsSearchTerm.toLowerCase()) ||
          (technician.isRegionTotal && regionsSearchTerm === ""),
      ),
    }))
    .filter((region) => region.children.length > 0);

  const totalTechnicians = filteredRegionsData.reduce(
    (total, region) => total + (region.children || []).length,
    0,
  );

  useEffect(() => {
    const fetchUnits = async () => {
      if (!group?.id) {
        setUnits([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${group.id}`,
        );

        const devicesWithMetadata = (response.data || []).map((device) => {
          let deviceRegion = null;
          let deviceTechnician = null;

          for (const region of regionsData) {
            for (const technician of region.children || []) {
              if (technician.children) {
                const foundDevice = technician.children.find(
                  (d) => d.id === device.id,
                );
                if (foundDevice) {
                  deviceRegion = region;
                  deviceTechnician = technician;
                  break;
                }
              }
            }
            if (deviceRegion) break;
          }

          return {
            ...device,
            regionId: deviceRegion?.id || null,
            regionName: deviceRegion?.name || null,
            technicianId: deviceTechnician?.id || null,
            technicianName: deviceTechnician?.name || null,
          };
        });

        setUnits(devicesWithMetadata);
        setCurrentView("all");
        setCurrentTechnician(null);
        setCurrentRegion(null);
      } catch (err) {
        console.error("Error fetching units:", err);
        setError("Failed to load units data");
        setUnits([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUnits();
  }, [group?.id, handleRefresh, regionsData]);

  const getDisplayUnits = () => {
    const findDevice = (id) => units.find((device) => device.id === id);

    if (currentView === "technician" && currentTechnician) {
      const ids = currentTechnician.children?.map((c) => c.id) || [];
      return ids.map(findDevice).filter(Boolean);
    } else if (currentView === "region" && currentRegion) {
      const allRegionUnitIds = [];

      const regionData = processedRegionsData.find(
        (r) => r.id === currentRegion.id,
      );
      const allTechnicians =
        regionData?.allChildrenIncludingHidden || currentRegion.children || [];

      allTechnicians.forEach((technician) => {
        if (!technician.isRegionTotal && technician.children) {
          allRegionUnitIds.push(
            ...technician.children.map((child) => child.id),
          );
        }
      });

      return allRegionUnitIds.map(findDevice).filter(Boolean);
    } else {
      return units;
    }
  };

  const displayUnits = getDisplayUnits();

  const filteredDisplayUnits = displayUnits.filter(
    (unit) =>
      unit.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.id?.toString().includes(searchTerm),
  );

  const filteredUnits = units.filter(
    (unit) =>
      unit.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.id?.toString().includes(searchTerm),
  );

  const getExportData = () => {
    const exportData = [];

    filteredRegionsData.forEach((region) => {
      region.children.forEach((technician) => {
        exportData.push({
          region: region.name || "N/A",
          technician: technician.isRegionTotal
            ? "Region Total"
            : technician.name || "N/A",
          unitCount: technician.isRegionTotal
            ? technician.unitCount
            : technician.children?.length || 0,
        });
      });
    });

    return exportData;
  };

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const copyToClipboard = () => {
    const exportData = getExportData();

    if (exportData.length === 0) {
      console.warn("No regions data to copy");
      return;
    }

    const headers = "Region\tTechnician\t# Units";
    const textContent = exportData
      .map((item) => `${item.region}\t${item.technician}\t${item.unitCount}`)
      .join("\n");

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => console.log("Regions & Technicians data copied to clipboard"))
      .catch((err) => console.error("Failed to copy:", err));

    handleExportMenuClose();
  };

  const exportToCSV = () => {
    const exportData = getExportData();

    if (exportData.length === 0) {
      console.warn("No regions data to export");
      return;
    }

    const csvContent = [
      ["Region", "Technician", "# Units"],
      ...exportData.map((item) => [
        item.region,
        item.technician,
        item.unitCount,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${group?.name || "Group"}_Regions_Technicians.csv`;
    link.click();

    handleExportMenuClose();
    console.log("Exported Regions & Technicians to CSV");
  };

  const exportToExcel = () => {
    const exportData = getExportData();

    if (exportData.length === 0) {
      console.warn("No regions data to export");
      return;
    }

    const headers = ["Region", "Technician", "# Units"];
    const rows = exportData.map((item) => [
      item.region,
      item.technician,
      item.unitCount,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Regions & Technicians");

    XLSX.writeFile(
      workbook,
      `${group?.name || "Group"}_Regions_Technicians.xlsx`,
    );

    handleExportMenuClose();
    console.log("Exported Regions & Technicians to Excel");
  };

  const exportToPDF = () => {
    const exportData = getExportData();

    if (exportData.length === 0) {
      console.warn("No regions data to export");
      return;
    }

    const doc = new jsPDF("p", "pt", "a4");

    doc.setFontSize(16);
    doc.text(`${group?.name || "Group"} - Regions & Technicians`, 40, 40);

    const headers = [["Region", "Technician", "# Units"]];
    const rows = exportData.map((item) => [
      item.region,
      item.technician,
      item.unitCount.toString(),
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 60,
      theme: "grid",
      styles: {
        fontSize: 10,
        cellPadding: 5,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 120 },
        2: { cellWidth: 80 },
      },
      didParseCell: function (data) {
        if (data.row.index > 0 && data.cell.raw === "Region Total") {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [240, 240, 240];
        }
      },
    });

    doc.save(`${group?.name || "Group"}_Regions_Technicians.pdf`);
    handleExportMenuClose();
    console.log("Exported Regions & Technicians to PDF");
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRegionsSearchChange = (event) => {
    setRegionsSearchTerm(event.target.value);
  };

  const handleShowUnits = (technician, region) => {
    if (technician.isRegionTotal || technician.name === "-") {
      setCurrentView("region");

      const fullRegion = processedRegionsData.find((r) => r.id === region.id);
      setCurrentRegion(fullRegion || region);
      setCurrentTechnician(null);

      console.log("Showing all units for region:", region);
    } else {
      setCurrentView("technician");
      setCurrentTechnician(technician);
      setCurrentRegion(region);

      console.log("Showing units for technician:", technician.name);
      console.log("Technician units:", technician.children);
    }
  };

  const getUnitsTableTitle = () => {
    if (currentView === "technician" && currentTechnician) {
      return `${currentTechnician.name}'s Units`;
    } else if (currentView === "region" && currentRegion) {
      return `${currentRegion.name}: All Units`;
    } else {
      return `${group?.name || "ROOT GROUP"}: Units`;
    }
  };

  const handleEditTechnician = (technician, region) => {
    if (technician.isRegionTotal) {
      console.log("Cannot edit region total row");
      return;
    }

    setEditTechnicianDialog({
      open: true,
      technician: {
        ...technician,
        region: region?.name || "",
        regionId: region?.id || "",
        group: group?.name || "ROOT GROUP",
      },
      currentRegion: region,
    });
  };

  const handleEditRegion = async (technician, region) => {
    setEditRegionDialog({
      open: true,
      technician: {
        ...technician,
        region: region?.name || "",
        regionId: region?.id || "",
        group: group?.name || "ROOT GROUP",
      },
      currentRegion: region,
    });
  };

  const handleSaveTechnician = (updatedTechnician) => {
    console.log("Saving technician:", updatedTechnician);

    setUpdater((prev) => !prev);
    setHandleRefresh(!handleRefresh);

    setEditTechnicianDialog({
      open: false,
      technician: null,
    });
  };

  const handleCloseEditTechnicianDialog = () => {
    setEditTechnicianDialog(false);
  };

  const handleSaveRegion = (updatedTechnician) => {
    console.log("Saving technician:", updatedTechnician);

    setUpdater((prev) => !prev);
    setHandleRefresh(!handleRefresh);

    setEditRegionDialog({
      open: false,
      technician: null,
    });
  };

  const handleCloseEditRegionDialog = () => {
    setEditRegionDialog(false);
  };

  const handleDeleteTechnician = async (technician) => {
    setDeletingTechnician(true);

    if (!technician?.id) {
      console.error("No technician selected for deletion");
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_GATEAWAY}/routes/${technician.id}/hard`,
        {
          data: {
            UI_Description: `${getUIDescription()}\n Delete Technician`, 
          },
        },
      );

      setRegionsData((prevRegions) =>
        prevRegions.map((region) => ({
          ...region,
          children: region.children.filter((tech) => tech.id !== technician.id),
        })),
      );

      toast.success("Technician deleted successfully");
    } catch (error) {
      console.error("Error deleting technician:", error);
      toast.error("Failed to delete technician");
    } finally {
      setConfirmTechnicianOpen(false);
      setSelectedTechnician(null);
      setDeletingTechnician(false);
      onDeleteTechnician();
    }
  };

  const handleDeleteRegion = async (region) => {
    setDeletingRegion(true);
    if (!region?.id) {
      console.error("No region selected for deletion");
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_GATEAWAY}/regions/${region.id}`,
        {
          data: {
            UI_Description: `${getUIDescription()}\n Delete Region`, 
          },
        },
      );

      setRegionsData((prevRegions) =>
        prevRegions.filter((r) => r.id !== region.id),
      );

      if (currentRegion?.id === region.id) {
        setCurrentView("all");
        setCurrentRegion(null);
        setCurrentTechnician(null);
      }

      toast.success("Region deleted successfully");
    } catch (error) {
      console.error("Error deleting region:", error);
      toast.error("Failed to delete region");
    } finally {
      setConfirmRegionOpen(false);
      setSelectedRegion(null);
      setRegionRefresher((prev) => prev + 1);
      setDeletingRegion(false);
      onDeleteRegion();
    }
  };

  const handleOpenAddTechnician = () => {
    setAddTechnicianDialog(true);
  };

  const handleCloseAddTechnician = () => {
    setAddTechnicianDialog(false);
  };

  const handleOpenAddRegion = () => {
    setAddRegionDialog(true);
  };

  const handleCloseAddRegion = () => {
    setAddRegionDialog(false);
  };

  const handleOpenAssign = (unit, region, technician) => {
    setSelectedUnit(unit);
    setSelectedRegion(region);
    setSelectedTechnician(technician);
    setAssignDialog(true);
  };

  const handleCloseAssign = () => {
    setAssignDialog(false);
    setSelectedUnit(null);
    setSearchTerm("");
    setHandleRefresh(!handleRefresh);
  };

  const handleTechnicianAdded = () => {
    setUpdater((prev) => !prev);
    setHandleRefresh(!handleRefresh);

    if (onAddTechnician) {
      onAddTechnician();
    }
  };

  const handleRegionAdded = () => {
    setUpdater((prev) => !prev);
    setRegionRefresher((prev) => prev + 1);

    if (onAddRegion) {
      onAddRegion();
    }
  };

  const hasData = filteredRegionsData.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: isSmall || isIpadPro ? "auto" : "84vh",
        overflow: isSmall || isIpadPro ? "visible" : "hidden",
        p: isSmall || isIpadPro ? 1 : 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 0.5, sm: 2, md: 2 },
          mb: 3,
          flexShrink: 0,
          flexDirection: isSmall || isIpadPro ? "column" : "row",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2, md: 2 },
            width: isSmall || isIpadPro ? "100%" : "auto",
          }}
        >
          <IconButton onClick={onBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{
              fontSize: { xs: "19px", sm: "15px", md: "16px" },
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
            fontWeight="bold"
          >
            Site Admin
          </Typography>
          <Chip
            label={`${group?.name || "ROOT GROUP"}: Regions & Technicians`}
            color="primary"
            size="small"
            sx={{ fontSize: { xs: "9px", sm: "15px", md: "16px" } }}
          />
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              justifyContent: "flex-end",
              mb: 1,
            }}
          >
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{
                color: "white",
                bgcolor: "#374151",
                "&:hover": { bgcolor: "#4b5563" },
                borderRadius: 1,
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Mobile: Drawer Menu */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              width: 280,
              bgcolor: "#1f2937",
              color: "white",
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Menu
              </Typography>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                sx={{ color: "white" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                fullWidth
                startIcon={<GroupIcon />}
                onClick={() => {
                  searchUserTable();
                  setDrawerOpen(false);
                }}
                sx={{
                  justifyContent: "flex-start",
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "14px",
                  bgcolor: "transparent",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#374151",
                  },
                }}
              >
                User List
              </Button>

              <Button
                fullWidth
                startIcon={<AccountTreeIcon />}
                onClick={() => {
                  precisionUnitTable();
                  setDrawerOpen(false);
                }}
                sx={{
                  justifyContent: "flex-start",
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "14px",
                  bgcolor: "#1565c0",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#1565c0",
                  },
                }}
              >
                Precision Unit Groups
              </Button>

              <Button
                fullWidth
                startIcon={<Diversity3Icon />}
                onClick={() => {
                  CustomerGroupTable();
                  setDrawerOpen(false);
                }}
                sx={{
                  justifyContent: "flex-start",
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "14px",
                  bgcolor: "transparent",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#374151",
                  },
                }}
              >
                Customer Groups
              </Button>

              <Button
                fullWidth
                startIcon={<AppsIcon />}
                onClick={() => {
                  ApplicationTable();
                  setDrawerOpen(false);
                }}
                sx={{
                  justifyContent: "flex-start",
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "14px",
                  bgcolor: "transparent",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#374151",
                  },
                }}
              >
                Applications
              </Button>

              <Button
                fullWidth
                startIcon={<FactCheckIcon />}
                onClick={() => {
                  AuditTable();
                  setDrawerOpen(false);
                }}
                sx={{
                  justifyContent: "flex-start",
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "14px",
                  bgcolor: "transparent",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#374151",
                  },
                }}
              >
                Audit
              </Button>
            </Box>
          </Box>
        </Drawer>
      </Box>

      {/* Regions & Technicians Layout */}
      <Grid
        container
        spacing={isSmall || isIpadPro ? 0 : 3}
        sx={{
          height: isSmall || isIpadPro ? "auto" : "calc(100vh - 120px)",
          overflow: isSmall || isIpadPro ? "visible" : "hidden",
        }}
      >
        {/* Regions & Technicians*/}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: isSmall || isIpadPro ? "100%" : "50%",
            height: isSmall || isIpadPro ? "auto" : "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            variant="outlined"
            sx={{
              height: isSmall || isIpadPro ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              mb: isSmall || isIpadPro ? 3 : 0,
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: isSmall || isIpadPro ? "visible" : "hidden",
              }}
            >
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Regions & Technicians
              </Typography>
              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Button
                  sx={{
                    borderRadius: 0, // ✅ remove rounding
                  }}
                  variant="outlined"
                  color="primary"
                  onClick={onEditGroup}
                  fullWidth={isSmall || isIpadPro}
                >
                  Edit Group
                </Button>
                <Button
                  sx={{
                    borderRadius: 0, // ✅ remove rounding
                  }}
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenAddRegion}
                  fullWidth={isSmall || isIpadPro}
                >
                  Add Region
                </Button>
                <Button
                  sx={{
                    borderRadius: 0, // ✅ remove rounding
                  }}
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenAddTechnician}
                  fullWidth={isSmall || isIpadPro}
                >
                  Add Technician
                </Button>
              </Box>

              {/* Search & Export Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 2,
                  flexWrap: "nowrap", // keep in one line
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexDirection: "row", // always row
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography component="span">Search:</Typography>
                  <TextField
                    size="small"
                    sx={{
                      width: 200,
                      "& .MuiOutlinedInput-root": { borderRadius: 0 },
                    }}
                    value={regionsSearchTerm}
                    onChange={handleRegionsSearchChange}
                    placeholder="Search technicians..."
                  />
                </Box>

                <Button
                  sx={{ borderRadius: 0, height: "36px" }}
                  variant="outlined"
                  color="white"
                  startIcon={<GetAppIcon />}
                  onClick={handleExportMenuOpen}
                >
                  Export
                </Button>
              </Box>

              {/* Export Menu */}
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleExportMenuClose}
              >
                <MenuItem onClick={copyToClipboard}>
                  <ListItemIcon>
                    <FileCopyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Copy</ListItemText>
                </MenuItem>
                <MenuItem onClick={exportToCSV}>
                  <ListItemIcon>
                    <DescriptionIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>CSV</ListItemText>
                </MenuItem>
                <MenuItem onClick={exportToExcel}>
                  <ListItemIcon>
                    <TableViewIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Excel</ListItemText>
                </MenuItem>
                <MenuItem onClick={exportToPDF}>
                  <ListItemIcon>
                    <PictureAsPdfIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>PDF</ListItemText>
                </MenuItem>
              </Menu>
              <TableContainer sx={{ flex: 1, overflow: "auto" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "black" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Region</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Technician
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}># Units</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Show Units
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Edit</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {regionsLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <CircularProgress color="#60a5fa" size={24} />
                          <Typography sx={{ mt: 1 }}>
                            Loading regions and technicians...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : filteredRegionsData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          {regionsSearchTerm
                            ? "No regions or technicians match your search"
                            : "No regions or technicians available"}
                        </TableCell>
                      </TableRow>
                    ) : !hasData ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          No data available in table
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRegionsData.map((region) =>
                        (region.children || []).map((technician, index) => (
                          <TableRow
                            key={technician.id}
                            hover
                            sx={{
                              backgroundColor: technician.isRegionTotal
                                ? "#252525"
                                : "inherit",
                              "& .MuiTableCell-root": {
                                fontWeight: technician.isRegionTotal
                                  ? "bold"
                                  : "normal",
                              },
                            }}
                          >
                            {index === 0 ? (
                              <TableCell
                                rowSpan={region.children?.length || 1}
                                sx={{
                                  fontWeight: "bold",
                                  borderRight: "2px solid #e0e0e0",
                                }}
                              >
                                {region.name || "N/A"}
                              </TableCell>
                            ) : null}
                            <TableCell>
                              {technician.isRegionTotal ? (
                                <Typography
                                  component="span"
                                  sx={{ fontStyle: "italic" }}
                                >
                                  {technician.name}
                                </Typography>
                              ) : (
                                technician.name || "N/A"
                              )}
                            </TableCell>
                            <TableCell>
                              {technician.isRegionTotal
                                ? technician.unitCount
                                : technician.children?.length || 0}
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={
                                  technician.isRegionTotal
                                    ? "Show All Region Units"
                                    : "Show Technician Units"
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleShowUnits(technician, region)
                                  }
                                  color="primary"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              {!technician.isRegionTotal &&
                              region.children.length !== 1 ? (
                                <Tooltip title="Edit Technician">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleEditTechnician(technician, region)
                                    }
                                    color="secondary"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Edit Region">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleEditRegion(technician, region)
                                    }
                                    color="secondary"
                                    sx={{
                                      backgroundColor: (theme) =>
                                        theme.palette.info.light,
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: (theme) =>
                                          theme.palette.info.main,
                                      },
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>
                              {!technician.isRegionTotal &&
                              region.children.length !== 1 ? (
                                <Tooltip title="Delete Technician">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      confirmDeleteRoute(technician)
                                    }
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Delete Entire Region">
                                  <IconButton
                                    size="medium"
                                    onClick={() => confirmDelete(region)}
                                    color="error"
                                    sx={{
                                      backgroundColor: "error.main",
                                      color: "white",
                                      "&:hover": {
                                        backgroundColor: "error.dark",
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        )),
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                variant="body2"
                sx={{ textAlign: "center", mt: 1, flexShrink: 0 }}
              >
                {regionsLoading
                  ? "Loading..."
                  : `Showing ${totalTechnicians} of ${totalTechnicians} entries`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* GPE & UNITS */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: { xs: "100%", sm: "100%", md: "48%" },
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1, sm: 3, md: 3 },
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: isIpadPro
                  ? "content" // whatever style you want for iPad
                  : { xs: "content", sm: "content", md: "flex" },
                gap: 3,
                flexShrink: 0,
              }}
            >
              {/* Group Information Section */}
              <Paper variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      Group Information
                    </Typography>
                  </Box>
                </CardContent>
                <CardContent>
                  <Box>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: "bold", width: "40%" }}
                            >
                              Name
                            </TableCell>
                            <TableCell>{group?.name || "ROOT GROUP"}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Description
                            </TableCell>
                            <TableCell>
                              {group?.description || "ROOT GROUP"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Date Created
                            </TableCell>
                            <TableCell>
                              {group?.date_created || "2025-10-02T19:10:54"}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </CardContent>
              </Paper>
              {/* Units Section */}
              <Card
                variant="outlined"
                sx={{ flex: 1, display: "flex", flexDirection: "column" }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      Units
                    </Typography>
                  </Box>
                  <Box>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Number of Units
                            </TableCell>
                            <TableCell>
                              {loading ? "Loading..." : units.length}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Divider sx={{ flexShrink: 0 }} />

            {/* Units Section */}
            <Card
              variant="outlined"
              sx={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    {getUnitsTableTitle()}
                  </Typography>
                  <Button
                    sx={{
                      borderRadius: 0, // ✅ remove rounding
                    }}
                    variant="outlined"
                    size="small"
                    onClick={() => setHandleRefresh(!handleRefresh)}
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress color="#60a5fa" size={16} />
                      ) : null
                    }
                  >
                    {loading ? "Refreshing..." : "Refresh"}
                  </Button>
                </Box>
                {error && (
                  <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                    {error}
                  </Alert>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ mr: 1, fontSize: "0.875rem" }}
                  >
                    Search:
                  </Typography>
                  <TextField
                    size="small"
                    sx={{
                      width: 200,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0, // ✅ remove rounding
                      },
                    }}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search units..."
                    disabled={loading}
                  />
                </Box>

                <TableContainer
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    maxHeight: isSmall || isIpadPro ? "70vh" : null,
                    marginBottom: isSmall || isIpadPro ? "10vh" : null,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "black" }}>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Unit Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Modem Serial Number
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Assign
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                            <CircularProgress color="#60a5fa" size={24} />
                            <Typography sx={{ mt: 1 }}>
                              Loading units...
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : filteredDisplayUnits.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                            {searchTerm
                              ? "No units match your search"
                              : "No units available"}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredDisplayUnits.map((unit, index) => (
                          <TableRow
                            key={unit.id}
                            hover
                            sx={{
                              backgroundColor:
                                index % 2 === 0 ? "#2f3136" : "#3a3c43",
                              "&:hover": {
                                backgroundColor: "#4a4d54",
                              },
                            }}
                          >
                            <TableCell>{unit.name || "N/A"}</TableCell>
                            <TableCell>{unit.id || "N/A"}</TableCell>
                            <TableCell>
                              <Button
                                sx={{
                                  borderRadius: 0, // ✅ remove rounding
                                }}
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                  handleOpenAssign(
                                    unit,
                                    selectedRegion,
                                    selectedTechnician,
                                  )
                                }
                              >
                                Assign
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", mt: 1, flexShrink: 0 }}
                >
                  {loading
                    ? "Loading..."
                    : `Showing ${filteredDisplayUnits.length} of ${units.length} entries`}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Edit Technician Dialog */}
      <EditTechnicianDialog
        open={editTechnicianDialog.open}
        onClose={handleCloseEditTechnicianDialog}
        technician={editTechnicianDialog.technician}
        groups={allGroups}
        group={group}
        regions={regions}
        onSave={handleSaveTechnician}
      />

      <EditRegionDialog
        open={editRegionDialog.open}
        onClose={handleCloseEditRegionDialog}
        technician={editRegionDialog.technician}
        groups={allGroups}
        group={group}
        onSave={handleSaveRegion}
      />

      {/* Add Technician Dialog */}
      <AddTechnician
        open={addTechnicianDialog}
        onClose={handleCloseAddTechnician}
        group={group}
        currentRegion={currentRegion}
        onAddTechnician={handleTechnicianAdded}
        regionRefresher={regionRefresher}
      />

      <AddRegion
        open={addRegionDialog}
        onClose={handleCloseAddRegion}
        group={group}
        onAddRegion={handleRegionAdded}
      />

      <AssignDevice
        device={selectedUnit}
        currentRegion={currentRegion}
        currentTechnician={currentTechnician}
        open={assignDialog}
        onClose={handleCloseAssign}
        group={group}
        onAddTechnician={handleTechnicianAdded}
        regionRefresher={regionRefresher}
      />

      <ConfirmDialog
        open={confirmRegionOpen}
        onClose={() => {
          setConfirmRegionOpen(false);
          setSelectedRegion(null);
        }}
        onConfirm={() => handleDeleteRegion(selectedRegion)}
        loading={deletingRegion}
      />

      <ConfirmDialog
        open={confirmTechnicianOpen}
        onClose={() => {
          setConfirmTechnicianOpen(false);
          setSelectedTechnician(null);
        }}
        onConfirm={() => handleDeleteTechnician(selectedTechnician)}
        loading={deletingTechnician}
      />
    </Box>
  );
};

export default GroupDetailView;
